import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';

const SnakeGame = () => {
  const { isDark } = useTheme();
  const gridSize = 15;

  const [snake, setSnake] = useState([{ x: 7, y: 7 }]);
  const [food, setFood] = useState({ x: 5, y: 5 });
  const [direction, setDirection] = useState('RIGHT');
  const [nextDirection, setNextDirection] = useState('RIGHT');
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [speed, setSpeed] = useState(150);
  const [isPaused, setIsPaused] = useState(false);

  // Generate posisi makanan acak
  const generateFood = useCallback(() => {
    const possiblePositions = [];
    for (let x = 0; x < gridSize; x++) {
      for (let y = 0; y < gridSize; y++) {
        const isOnSnake = snake.some(segment => segment.x === x && segment.y === y);
        if (!isOnSnake) possiblePositions.push({ x, y });
      }
    }
    if (possiblePositions.length === 0) return { x: -1, y: -1 };
    return possiblePositions[Math.floor(Math.random() * possiblePositions.length)];
  }, [snake, gridSize]);

  // Inisialisasi ulang game
  const initGame = useCallback(() => {
    setSnake([{ x: 7, y: 7 }]);
    setFood(generateFood());
    setDirection('RIGHT');
    setNextDirection('RIGHT');
    setGameOver(false);
    setScore(0);
    setSpeed(150);
    setIsPaused(false);
  }, [generateFood]);

  // Kontrol Keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) {
        e.preventDefault();
      }

      if (e.key === ' ') {
        setIsPaused(prev => !prev);
        return;
      }

      if (gameOver && e.key === 'Enter') {
        initGame();
        return;
      }

      if (isPaused) return;

      switch (e.key) {
        case 'ArrowUp':
          if (direction !== 'DOWN') setNextDirection('UP');
          break;
        case 'ArrowDown':
          if (direction !== 'UP') setNextDirection('DOWN');
          break;
        case 'ArrowLeft':
          if (direction !== 'RIGHT') setNextDirection('LEFT');
          break;
        case 'ArrowRight':
          if (direction !== 'LEFT') setNextDirection('RIGHT');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [direction, gameOver, isPaused, initGame]);

  // Game Loop
  useEffect(() => {
    if (gameOver || isPaused) return;

    const gameLoop = setInterval(() => {
      setSnake(prevSnake => {
        setDirection(nextDirection);
        const head = { ...prevSnake[0] };

        switch (nextDirection) {
          case 'UP': head.y -= 1; break;
          case 'DOWN': head.y += 1; break;
          case 'LEFT': head.x -= 1; break;
          case 'RIGHT': head.x += 1; break;
          default: break;
        }

        // Tabrakan tembok
        if (head.x < 0 || head.x >= gridSize || head.y < 0 || head.y >= gridSize) {
          setGameOver(true);
          setHighScore(prev => Math.max(prev, score));
          return prevSnake;
        }

        // Tabrakan badan sendiri
        if (prevSnake.slice(1).some(segment => segment.x === head.x && segment.y === head.y)) {
          setGameOver(true);
          setHighScore(prev => Math.max(prev, score));
          return prevSnake;
        }

        const newSnake = [head, ...prevSnake];

        // Makan makanan
        if (head.x === food.x && head.y === food.y) {
          setFood(generateFood());
          setScore(prev => {
            const newScore = prev + 1;
            if (newScore > 0 && newScore % 5 === 0) {
              setSpeed(prevSpeed => Math.max(50, prevSpeed - 10));
            }
            return newScore;
          });
        } else {
          newSnake.pop();
        }

        return newSnake;
      });
    }, speed);

    return () => clearInterval(gameLoop);
  }, [nextDirection, food, gameOver, generateFood, score, speed, isPaused, gridSize]);

  const handleSwipe = (newDirection) => {
    if (isPaused) return;
    switch (newDirection) {
      case 'UP': if (direction !== 'DOWN') setNextDirection('UP'); break;
      case 'DOWN': if (direction !== 'UP') setNextDirection('DOWN'); break;
      case 'LEFT': if (direction !== 'RIGHT') setNextDirection('LEFT'); break;
      case 'RIGHT': if (direction !== 'LEFT') setNextDirection('RIGHT'); break;
      default: break;
    }
  };

  // Styles berdasarkan tema
  const titleClr   = isDark ? 'text-white' : 'text-gray-800';
  const textMuted  = isDark ? 'text-slate-400' : 'text-gray-500';
  const scoreClr   = isDark ? 'text-blue-400' : 'text-blue-600';
  const gridBorder = isDark ? 'border-[#1E3A5F] bg-[#0D1520]' : 'border-[#1E293B] bg-white';
  const cellEmpty  = isDark ? 'bg-[#111C2C] border-[#0D1520]' : 'bg-slate-100 border-white';
  const mobileBtn  = isDark ? 'bg-[#111C2C] text-slate-200 border-[#1E3A5F]' : 'bg-gray-200 text-gray-800 border-[#1E293B]';

  const renderGrid = () => {
    const grid = [];
    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        const isSnake = snake.some(s => s.x === x && s.y === y);
        const isHead = snake[0].x === x && snake[0].y === y;
        const isFood = food.x === x && food.y === y;

        let cellClass = 'w-4 h-4 md:w-5 md:h-5 border ';

        if (isHead) {
          cellClass += 'bg-emerald-500 border-emerald-400';
        } else if (isSnake) {
          cellClass += 'bg-emerald-600/80 border-emerald-500';
        } else if (isFood) {
          cellClass += 'bg-rose-500 border-rose-400 rounded-full scale-90';
        } else {
          cellClass += cellEmpty;
        }

        grid.push(<div key={`${x}-${y}`} className={cellClass} />);
      }
    }
    return grid;
  };

  return (
    <div className="max-w-md mx-auto text-center">
      <h3 className={`text-xl md:text-2xl font-bold mb-4 pixel-font ${titleClr}`}>
        🐍 SNAKE GAME
      </h3>

      {/* Scoreboard */}
      <div className="grid grid-cols-3 gap-2 mb-4 bg-[var(--bg-muted)] p-3 pixel-border">
        <div>
          <div className={`text-[10px] pixel-font ${textMuted}`}>SCORE</div>
          <div className={`text-lg font-bold ${scoreClr}`}>{score}</div>
        </div>
        <div>
          <div className={`text-[10px] pixel-font ${textMuted}`}>HIGH SCORE</div>
          <div className="text-lg font-bold text-amber-500">{highScore}</div>
        </div>
        <div>
          <div className={`text-[10px] pixel-font ${textMuted}`}>SPEED</div>
          <div className="text-lg font-bold text-rose-500">{Math.round(1000 / speed)}x</div>
        </div>
      </div>

      {/* Canvas / Grid Area */}
      <div className="flex justify-center mb-4">
        <div
          className={`grid gap-0 pixel-border p-1.5 ${gridBorder}`}
          style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
        >
          {renderGrid()}
        </div>
      </div>

      {/* Overlay Game Over */}
      {gameOver && (
        <div className="mb-4 p-4 bg-rose-950/80 border-2 border-rose-500 pixel-border text-rose-100">
          <div className="text-sm font-bold pixel-font mb-1">GAME OVER!</div>
          <div className="text-xs mb-3">Skor Akhir: {score}</div>
          <button
            className="pixel-button bg-emerald-600 text-white px-4 py-1.5 text-xs font-bold pixel-font hover:bg-emerald-500"
            onClick={initGame}
          >
            MAIN LAGI
          </button>
        </div>
      )}

      {/* Kontrol Tombol */}
      <div className="flex justify-center gap-3 mb-4">
        <button
          className="pixel-button bg-slate-700 text-white px-4 py-2 text-xs font-bold pixel-font hover:bg-slate-600"
          onClick={() => setIsPaused(!isPaused)}
        >
          {isPaused ? 'RESUME' : 'PAUSE'}
        </button>
        <button
          className="pixel-button bg-blue-600 text-white px-4 py-2 text-xs font-bold pixel-font hover:bg-blue-500"
          onClick={initGame}
        >
          RESTART
        </button>
      </div>

      {/* Kontrol D-Pad untuk Layar Sentuh / Mobile */}
      <div className="md:hidden mt-4">
        <div className={`text-xs ${textMuted} mb-2 pixel-font`}>KONTROL LAYAR SENTUH:</div>
        <div className="grid grid-cols-3 gap-2 max-w-[180px] mx-auto">
          <div></div>
          <button className={`pixel-button ${mobileBtn} p-3 font-bold`} onClick={() => handleSwipe('UP')}>↑</button>
          <div></div>

          <button className={`pixel-button ${mobileBtn} p-3 font-bold`} onClick={() => handleSwipe('LEFT')}>←</button>
          <div className={`pixel-border ${mobileBtn} flex items-center justify-center font-bold text-xs`}>🎮</div>
          <button className={`pixel-button ${mobileBtn} p-3 font-bold`} onClick={() => handleSwipe('RIGHT')}>→</button>

          <div></div>
          <button className={`pixel-button ${mobileBtn} p-3 font-bold`} onClick={() => handleSwipe('DOWN')}>↓</button>
          <div></div>
        </div>
      </div>

      <div className={`hidden md:block text-xs ${textMuted} mt-3`}>
        Gunakan tombol panah untuk bergerak • Spasi untuk Pause • Enter untuk Restart
      </div>
    </div>
  );
};

export default SnakeGame;